/**
 * DateRangeFilter — o seletor de período de /relatorios.
 *
 * A lista de presets e a matemática das faixas vivem em `@/lib/reportPeriod`
 * (compartilhadas com o rótulo dos totais e com a definição de semana usada pela
 * Agenda e pelo Dashboard). Aqui só há o widget.
 *
 * O preset é CONTROLADO pela página, não guardado aqui: o rótulo do total
 * ("Total nesta semana") precisa saber qual preset está ativo. Deduzir o rótulo
 * pela quantidade de dias não distingue "Hoje" de "Ontem" nem "Esta semana" de
 * "Últimos 7 dias" — e foi deduzindo assim que o app passou a escrever "nos
 * últimos 12 meses" com "Últimos 6 meses" selecionado (armadilha #45).
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import {
  PERIOD_PRESET_LABELS,
  PERIOD_PRESET_ORDER,
  resolvePeriodPreset,
  type WeekStartsOn,
  type ReportPeriodPreset,
} from '@/lib/reportPeriod';

export type DateRange = {
  from: Date;
  to: Date;
};

/** Mantido para os consumidores antigos do tipo (a fonte é `reportPeriod`). */
export type PresetOption = ReportPeriodPreset;

interface DateRangeFilterProps {
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  preset: ReportPeriodPreset;
  onPresetChange: (preset: ReportPeriodPreset) => void;
  weekStartsOn?: WeekStartsOn;
}

export function DateRangeFilter({
  dateRange,
  onDateRangeChange,
  preset,
  onPresetChange,
  weekStartsOn = 0,
}: DateRangeFilterProps) {
  const [isCustomOpen, setIsCustomOpen] = useState(false);

  const handlePresetChange = (next: ReportPeriodPreset) => {
    onPresetChange(next);
    if (next === 'custom') {
      setIsCustomOpen(true);
      return;
    }
    const range = resolvePeriodPreset(next, new Date(), weekStartsOn);
    if (range) onDateRangeChange(range);
  };

  const formatDateRange = () => {
    if (preset !== 'custom') return PERIOD_PRESET_LABELS[preset] ?? 'Selecionar período';
    return `${format(dateRange.from, 'dd/MM/yy', { locale: ptBR })} - ${format(dateRange.to, 'dd/MM/yy', { locale: ptBR })}`;
  };

  return (
    <div className="flex items-center gap-2">
      <Select value={preset} onValueChange={(v) => handlePresetChange(v as ReportPeriodPreset)}>
        <SelectTrigger className="h-10 w-auto gap-1 text-xs">
          <CalendarIcon className="h-3.5 w-3.5" />
          <SelectValue>{formatDateRange()}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {PERIOD_PRESET_ORDER.map((value) => (
            <SelectItem key={value} value={value}>
              {PERIOD_PRESET_LABELS[value]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {preset === 'custom' && (
        <Popover open={isCustomOpen} onOpenChange={setIsCustomOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="h-10 text-xs">
              <CalendarIcon className="h-3.5 w-3.5 mr-1" />
              {format(dateRange.from, 'dd/MM', { locale: ptBR })} - {format(dateRange.to, 'dd/MM', { locale: ptBR })}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <div className="flex flex-col sm:flex-row gap-2 p-3">
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">De</p>
                <Calendar
                  mode="single"
                  selected={dateRange.from}
                  onSelect={(date) => date && onDateRangeChange({ ...dateRange, from: date })}
                  disabled={(date) => date > new Date() || date > dateRange.to}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </div>
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Até</p>
                <Calendar
                  mode="single"
                  selected={dateRange.to}
                  onSelect={(date) => date && onDateRangeChange({ ...dateRange, to: date })}
                  disabled={(date) => date > new Date() || date < dateRange.from}
                  className={cn("p-3 pointer-events-auto")}
                />
              </div>
            </div>
            <div className="border-t p-3">
              <Button3D size="sm" className="w-full" onClick={() => setIsCustomOpen(false)}>
                Aplicar
              </Button3D>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
